import aa from 'search-insights'
import { TrackingOptions, TrackingPayload } from './types'

const DAYS = 1000 * 60 * 60 * 24

type InitPayloadProps = {
  userToken: string
  index: string
}
export default class AlgoliaInsight {
  options: TrackingOptions
  isLogged: boolean
  customerId: string | null
  userToken: string | undefined

  constructor (options: TrackingOptions) {
    this.options = options
    this.isLogged = window.GM_STATE.customer.logged
    this.customerId = window.GM_STATE.customer?.id || null
  }

  extractGoogleAnalyticsUserIdFromCookie (gaCookie: string) {
    if (gaCookie) {
      // Remove the Google Analytics tracker from the device ID.
      const userIdParts = gaCookie.split('.').slice(-2)

      if (userIdParts.length === 2) {
        return userIdParts[0]
      }
    }

    return undefined
  }

  getBrowserCookie (cookieName: string) {
    if (typeof document !== 'object' || typeof document.cookie !== 'string') {
      return undefined
    }

    const name = cookieName + '='

    // Safely decode the cookie string with error handling
    let decodedCookie: string

    try {
      decodedCookie = decodeURIComponent(document.cookie)
    } catch (error) {
      // If decoding fails, use the raw cookie string
      decodedCookie = document.cookie
    }

    const ca = decodedCookie.split(';')

    for (let i = 0; i < ca.length; i++) {
      let c = ca[i]

      while (c.charAt(0) === ' ') {
        c = c.substring(1)
      }

      if (c.indexOf(name) === 0) {
        return c.substring(name.length, c.length)
      }
    }

    return undefined
  }

  getGoogleAnalyticsUserIdFromBrowserCookie (cookieName: string) {
    const browserCookie = this.getBrowserCookie(cookieName)

    if (!browserCookie) {
      return undefined
    }

    return this.extractGoogleAnalyticsUserIdFromCookie(browserCookie)
  }

  init () {
    const config = {
      appId: this.options.siteId,
      apiKey: this.options.apiKey,
      useCookie: true,
      cookieDuration: 90 * DAYS
    }

    aa('init', config)
    this.initUserToken()
  }

  initUserToken () {
    const userToken = this.customerId || this.getGoogleAnalyticsUserIdFromBrowserCookie('_ga')

    aa('setUserToken', userToken as string)
    this.setUserToken(userToken as string)
    this.userToken = userToken
  }

  getUserToken () {
    return localStorage.getItem('algolia-user-id') || this.userToken
  }

  setUserToken (token: string, STORAGE_KEY = 'algolia-user-id') {
    localStorage.setItem(STORAGE_KEY, token)
  }

  getEventNameByCustomer (customerIsLogged: string, customerIsGuest: string) {
    if (this.isLogged) {
      return customerIsLogged
    } else {
      return customerIsGuest
    }
  }

  getClickEventPayload (payload: TrackingPayload, initPayload: InitPayloadProps) {
    return {
      ...initPayload,
      eventType: 'click',
      eventName: this.getEventNameByCustomer('Customer.click', 'Guest.click'),
      ...payload
    }
  }

  getConversionEventPayload (payload: TrackingPayload, initPayload: InitPayloadProps) {
    return {
      ...initPayload,
      eventType: 'conversion',
      eventName: this.getEventNameByCustomer(
        'Customer.conversion',
        'Guest.conversion'
      ),
      ...payload
    }
  }

  getAddToCartConvertedEventPayload (payload: TrackingPayload, initPayload: InitPayloadProps) {
    return {
      ...initPayload,
      eventType: 'conversion',
      eventName: this.getEventNameByCustomer('Customer.atc', 'Guest.atc'),
      ...payload
    }
  }

  getViewedEventPayload (payload: TrackingPayload, initPayload: InitPayloadProps) {
    return {
      ...initPayload,
      eventType: 'view',
      eventName: this.getEventNameByCustomer('Customer.view', 'Guest.view'),
      ...payload
    }
  }

  getInitEventPayload () : InitPayloadProps {
    const token = `${this.userToken || this.getUserToken()}`
    let indexPrefix: string

    if (window.Shopify.shop === 'nutra-organics-us.myshopify.com') {
      indexPrefix = 'shopifyus_'
    } else {
      indexPrefix = 'shopify_'
    }

    return {
      userToken: token,
      index: `${indexPrefix}products`
    }
  }

  getAddedToCartEventPayload (payload: TrackingPayload, initPayload: InitPayloadProps) {
    return {
      ...initPayload,
      eventType: 'conversion',
      eventName: this.getEventNameByCustomer('Customer.added', 'Guest.added'),
      ...payload
    }
  }

  sendEvents (payload: TrackingPayload, include = 'all', eventName = 'sendEvents') {
    const initPayload = this.getInitEventPayload()
    let sendPayload = []

    switch (include) {
      case 'click':
        sendPayload.push(this.getClickEventPayload(payload, initPayload))
        break
      case 'viewed':
        sendPayload.push(this.getViewedEventPayload(payload, initPayload))
        break
      case 'conversion':
        sendPayload.push(this.getConversionEventPayload(payload, initPayload))
        break
      case 'atc':
        sendPayload.push(this.getAddToCartConvertedEventPayload(payload, initPayload))
        break
      case 'added':
        sendPayload.push(this.getAddedToCartEventPayload(payload, initPayload))
        break
      case 'all':
        sendPayload.push(this.getClickEventPayload(payload, initPayload))
        sendPayload.push(this.getViewedEventPayload(payload, initPayload))
        sendPayload.push(this.getConversionEventPayload(payload, initPayload))
        break
    }

    if (sendPayload.length === 1) {
      // @ts-expect-error: Unreachable code error
      sendPayload = sendPayload[0]
    }

    // @ts-expect-error: Unreachable code error
    aa(eventName, sendPayload)
  }
}
